import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral } from "app/themes/colors";
import { EarIcon } from "components/icons";
import type { Patient } from "services/types/patients.type";
import { formatBackendDateOnly } from "utils/common";

import TextCaption from "./text-caption.component";

interface PatientHearingAidsProps {
  patient: Patient;
}

export const PatientHearingAids: React.FC<PatientHearingAidsProps> = ({
  patient
}) => {
  const theme = useTheme();
  const patientHaveHearingAids = Boolean(
    patient?.left_ear_hearing_aid || patient?.right_ear_hearing_aid
  );

  return (
    <Stack gap={1} sx={{ background: misc.mutedPurple }}>
      <Stack gap={1} flexDirection="row">
        <Typography variant="body3" fontWeight="700" pb={0.5}>
          Hearing Aids
        </Typography>
        {!patientHaveHearingAids && (
          <Typography variant="body3">No Active Hearing Aids</Typography>
        )}
      </Stack>
      {patientHaveHearingAids && (
        <Stack gap={2} pb={0.5}>
          {patient?.left_ear_hearing_aid && (
            <Grid
              container
              xs={12}
              p={1.5}
              columnGap={1}
              sx={{
                border: `1px solid ${neutral[100]}`,
                borderRadius: "8px",
                lineHeight: 1.6,
                background: neutral[50],
                boxShadow: theme.shadows[3]
              }}
            >
              <Grid xs={3} display="flex" alignItems="center" pb={1}>
                <EarIcon />
                <Typography
                  variant="body2"
                  component="div"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  Left
                </Typography>
              </Grid>
              <Grid xs={8} textAlign="end">
                Delivery Date:{" "}
                {patient?.left_ear_hearing_aid?.delivered_at_display
                  ? formatBackendDateOnly(
                      patient?.left_ear_hearing_aid?.delivered_at_display,
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
              <Grid xs={12}>{patient?.left_ear_hearing_aid?.display_name}</Grid>
              <Grid xs={12}>
                <TextCaption>Serial Number:</TextCaption>{" "}
                {patient?.left_ear_hearing_aid?.serial_number || "-"}
              </Grid>
              <Grid xs={12}>
                <TextCaption>MFR Warranty Date:</TextCaption>{" "}
                {patient?.left_ear_hearing_aid
                  ?.manufacturer_warranty_expiration_date
                  ? formatBackendDateOnly(
                      String(
                        patient?.left_ear_hearing_aid
                          ?.manufacturer_warranty_expiration_date
                      ),
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
            </Grid>
          )}

          {patient?.right_ear_hearing_aid && (
            <Grid
              container
              xs={12}
              p={1.5}
              sx={{
                border: `1px solid ${neutral[100]}`,
                borderRadius: "8px",
                lineHeight: 1.6,
                background: neutral[50],
                boxShadow: theme.shadows[3]
              }}
            >
              <Grid xs={3} display="flex" alignItems="center" pb={1}>
                <EarIcon />
                <Typography
                  variant="body2"
                  component="div"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  Right
                </Typography>
              </Grid>
              <Grid xs={8} textAlign="end">
                Delivery Date:{" "}
                {patient?.right_ear_hearing_aid?.delivered_at_display
                  ? formatBackendDateOnly(
                      patient?.right_ear_hearing_aid?.delivered_at_display,
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
              <Grid xs={12}>
                {patient?.right_ear_hearing_aid?.display_name}
              </Grid>
              <Grid xs={12}>
                <TextCaption>Serial Number:</TextCaption>{" "}
                {patient?.right_ear_hearing_aid?.serial_number || "-"}
              </Grid>
              <Grid xs={12}>
                <TextCaption>MFR Warranty Date:</TextCaption>{" "}
                {patient?.right_ear_hearing_aid
                  ?.manufacturer_warranty_expiration_date
                  ? formatBackendDateOnly(
                      String(
                        patient?.right_ear_hearing_aid
                          ?.manufacturer_warranty_expiration_date
                      ),
                      "MMM dd, yyyy"
                    )
                  : "-"}
              </Grid>
            </Grid>
          )}
        </Stack>
      )}
    </Stack>
  );
};
